import { Icon } from "@iconify/react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Loader } from "@/components/motion/loader";
import { Button } from "@/components/ui/button";
import { type TopbarIslandOperationState, useTopbarIsland } from "@/features/workbench/topbar-island-context";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";

const RESULT_PRESENTATION = {
  error: {
    className: "text-destructive",
    icon: "line-md:alert-circle-twotone-loop",
  },
  info: {
    className: "text-primary",
    icon: "line-md:question-circle-twotone",
  },
  success: {
    className: "text-success",
    icon: "line-md:circle-twotone-to-confirm-circle-twotone-transition",
  },
  warning: {
    className: "text-warning",
    icon: "line-md:alert-twotone-loop",
  },
} as const;

type TopbarIslandOperationViewProps = {
  operation: TopbarIslandOperationState | null;
};

/** Generic prompt → loading → result presentation. Business owners drive phase changes through the island context. */
export function TopbarIslandOperationView({ operation }: TopbarIslandOperationViewProps) {
  const { dismiss } = useTopbarIsland();
  const { t } = useI18n();
  const reduceMotion = useReducedMotion();

  if (!operation) return null;

  const prompt = operation.phase === "prompt";
  const loading = operation.phase === "loading";
  const resultPresentation = operation.phase === "result" ? RESULT_PRESENTATION[operation.tone] : null;
  const icon = prompt ? (operation.icon ?? "line-md:alert-circle-twotone-loop") : (resultPresentation?.icon ?? "line-md:information-twotone");
  const iconClassName = prompt || loading ? "text-primary" : resultPresentation?.className;

  return (
    <AnimatePresence initial={false} mode="popLayout">
      <motion.div
        key={`${operation.id}:${operation.phase}`}
        animate={{ opacity: 1, y: 0 }}
        aria-busy={loading}
        className={cn("flex gap-3 px-4 py-3", prompt ? "w-88 items-start" : "w-80 items-center")}
        exit={reduceMotion ? { opacity: 1 } : { opacity: 0, y: -4 }}
        initial={reduceMotion ? false : { opacity: 0, y: 5 }}
        transition={reduceMotion ? { duration: 0 } : { bounce: 0.1, duration: 0.35, type: "spring" }}
      >
        <motion.span
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          className={cn("grid size-8 shrink-0 place-items-center", iconClassName)}
          initial={reduceMotion ? false : { opacity: 0, rotate: -12, scale: 0.72 }}
          transition={reduceMotion ? { duration: 0 } : { bounce: 0.2, duration: 0.45, type: "spring" }}
        >
          {loading ? (
            <Loader className="text-primary" label={operation.title} size={20} speed={0.9} variant="comet" />
          ) : (
            <Icon aria-hidden className="size-5" icon={icon} />
          )}
        </motion.span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-5 text-foreground">{operation.title}</p>
          {operation.description ? <p className="mt-0.5 line-clamp-2 text-xs leading-4 text-muted-foreground">{operation.description}</p> : null}
          {prompt ? (
            <div className="mt-2 flex items-center gap-1.5">
              <Button size="xs" onClick={() => void operation.onConfirm()}>
                {operation.confirmLabel}
              </Button>
              {operation.cancelLabel && operation.onCancel ? (
                <Button size="xs" variant="ghost" onClick={operation.onCancel}>
                  {operation.cancelLabel}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        {operation.phase === "result" ? (
          <Button aria-label={t("shell.dismissOperation")} size="icon-xs" variant="ghost" onClick={() => dismiss(operation.id)}>
            <Icon aria-hidden icon="tabler:x" />
          </Button>
        ) : null}
      </motion.div>
    </AnimatePresence>
  );
}
